import type { AdminRole } from '@echo-work-platform/shared-types';

export interface AdminRoleConfig {
  rolesClaim: string;
  roleNames: { admin: string; aiAdmin: string; auditor: string };
  fallback: boolean;
}

// Mirror of the server's extractRoles (auth/roles.ts): a dotted path into the token claims.
export function extractRawRoles(claims: Record<string, unknown> | undefined, claimPath: string): string[] {
  if (!claims) return [];
  const segments = claimPath.split('.').filter((s) => s.length > 0);
  let current: unknown = claims;
  for (const seg of segments) {
    if (typeof current !== 'object' || current === null) return [];
    current = (current as Record<string, unknown>)[seg];
  }
  if (typeof current === 'string') return [current];
  if (!Array.isArray(current)) return [];
  return current.filter((e): e is string => typeof e === 'string');
}

// Mirror of the server's resolveAdminRoles + withAdminFallback, so the UI never offers a
// surface the backend would 403. Case-insensitive; the fallback promotes a role-less token.
export function resolveAdminRoles(rawRoles: string[], config: AdminRoleConfig): AdminRole[] {
  const norm = (s: string) => s.trim().toLowerCase();
  const table: Array<[string, AdminRole]> = [
    [norm(config.roleNames.admin), 'ADMIN'],
    [norm(config.roleNames.aiAdmin), 'AI_ADMIN'],
    [norm(config.roleNames.auditor), 'AUDITOR'],
  ];
  const resolved = new Set<AdminRole>();
  for (const raw of rawRoles) {
    const r = norm(raw);
    for (const [name, role] of table) if (name === r) resolved.add(role);
  }
  if (resolved.size === 0 && config.fallback) resolved.add('ADMIN');
  return [...resolved];
}

// The landing route for a role, so a redirect or the forbidden notice sends the user somewhere
// they can actually open (an AUDITOR has no dashboard, an AI_ADMIN starts at Providers).
export function homeRouteFor(roles: AdminRole[]): string {
  if (roles.includes('ADMIN')) return '/';
  if (roles.includes('AI_ADMIN')) return '/providers';
  if (roles.includes('AUDITOR')) return '/audit-log';
  return '/';
}
